import React, { useEffect, useRef, useState } from "react";
import { SupplierInfo } from "../types/supplierInfo.types";
import { SaveIcon } from '@/shared/components/icons/Icons';
import { getItem } from '@/shared/utils/storage';
import { commonCodeApi } from '@/shared/api/commonCodeApi';
import { CommonCodeDto } from '@/shared/types/commonCode.types';

interface BasicInfoSectionProps {
    supplierInfo: SupplierInfo;
    saving: boolean;
    onFieldChange: (field: keyof SupplierInfo | Partial<SupplierInfo>, value?: string) => void;
    onSave: () => void;
}

const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
                                                               supplierInfo,
                                                               saving,
                                                               onFieldChange,
                                                               onSave,
                                                           }) => {
    const userType = getItem<string>('USER_TYPE');
    const isAdmin = userType === 'HEADQUARTERS';

    const [gradeCodes, setGradeCodes] = useState<CommonCodeDto[]>([]);
    const hasFetchedGradeCodes = useRef(false);

    useEffect(() => {
        if (hasFetchedGradeCodes.current) return;
        let isCancelled = false;
        const fetchGradeCodes = async () => {
            try {
                const response = await commonCodeApi.getCommonCodeList('SUPP_GRADE');
                if (!isCancelled) {
                    setGradeCodes(response.codes || []);
                    hasFetchedGradeCodes.current = true;
                }
            } catch {
                // 등급 코드 조회 실패 시 기본값 유지
            }
        };
        fetchGradeCodes();
        return () => { isCancelled = true; };
    }, []);

    return (
        <div className="detail-card">
            <div className="detail-card-header">
                <i className="fas fa-building card-icon" />
                <h3 className="card-title">기본 정보</h3>
            </div>

            <div className="info-table">
                <div className="info-row">
                    <div className="info-label">협력사명</div>
                    <div className="info-value">{supplierInfo.supplierName || '-'}</div>
                    <div className="info-label">협력사 아이디</div>
                    <div className="info-value">{supplierInfo.supplierId || '-'}</div>
                </div>

                <div className="info-row">
                    <div className="info-label">협력사 타입</div>
                    <div className="info-value">
                        {isAdmin ? (
                            <select
                                value={
                                    supplierInfo.suppType === '가맹' ? 'F' :
                                        supplierInfo.suppType === '직영' ? 'D' :
                                            supplierInfo.suppType === 'F' ? 'F' :
                                                supplierInfo.suppType === 'D' ? 'D' : 'D'
                                }
                                onChange={(e) => onFieldChange('suppType', e.target.value)}
                            >
                                <option value="D">직영</option>
                                <option value="F">가맹</option>
                            </select>
                        ) : (
                            supplierInfo.suppType || '-'
                        )}
                    </div>
                    <div className="info-label">협력사 등급</div>
                    <div className="info-value">
                        {isAdmin ? (
                            <select
                                value={supplierInfo.suppLvl || ''}
                                onChange={(e) => onFieldChange('suppLvl', e.target.value)}
                            >
                                <option value="">선택하세요</option>
                                {gradeCodes.map((grade) => (
                                    <option key={grade.comCode} value={grade.comCode}>
                                        {grade.comCdName}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            supplierInfo.supplierGrade || '-'
                        )}
                    </div>
                </div>

                <div className="info-row">
                    <div className="info-label">표준권역</div>
                    <div className="info-value">{supplierInfo.standardArea || '-'}</div>
                    <div className="info-label">협력사 세트수</div>
                    <div className="info-value">
                        {isAdmin ? (
                            <input
                                type="number"
                                value={supplierInfo.slotSetCnt || ''}
                                onChange={(e) => onFieldChange('slotSetCnt', e.target.value)}
                                placeholder="세트수를 입력하세요"
                                min="0"
                            />
                        ) : (
                            supplierInfo.slotSetCnt || '-'
                        )}
                    </div>
                </div>

                <div className="info-row">
                    <div className="info-label">상태</div>
                    <div className="info-value">{supplierInfo.status || '-'}</div>
                    <div className="info-label">등록일</div>
                    <div className="info-value">{supplierInfo.registrationDate || '-'}</div>
                </div>

                <div className="info-row">
                    <div className="info-label">운영종료일</div>
                    <div className="info-value">{supplierInfo.operationEndDate || '-'}</div>
                    <div className="info-label">배달권역</div>
                    <div className="info-value">{supplierInfo.deliveryArea || '-'}</div>
                </div>

                <div className="info-row">
                    <div className="info-label">전화번호</div>
                    <div className="info-value">
                        <input
                            type="text"
                            value={supplierInfo.mobilePhone || ''}
                            onChange={(e) => onFieldChange('mobilePhone', e.target.value)}
                            placeholder="휴대폰 번호를 입력하세요"
                        />
                    </div>
                    <div className="info-label">협력사 주소</div>
                    <div className="info-value">
                        <input
                            type="text"
                            value={supplierInfo.address || ''}
                            onChange={(e) => onFieldChange('address', e.target.value)}
                            placeholder="협력사 주소를 입력하세요"
                        />
                    </div>
                </div>
            </div>

            <div className="section-actions">
                <button className="save-btn" onClick={onSave} disabled={saving}>
                    <SaveIcon width={16} height={16} />
                    <span>{saving ? "저장 중..." : "저장"}</span>
                </button>
            </div>
        </div>
    );
};

export default BasicInfoSection;
